import type { ComponentType } from "react";
import { NetWorthHeroWidget } from "../../components/dashboard/widgets/net-worth-hero.js";
import { BreakdownWidget } from "../../components/dashboard/widgets/breakdown.js";
import { CashFlowSparklineWidget } from "../../components/dashboard/widgets/cash-flow-sparkline.js";
import { DecisionsRevisitWidgetWrapper } from "../../components/dashboard/widgets/decisions-revisit.js";
import { MessagesWidget } from "../../components/dashboard/widgets/messages.js";
import { ContributionPriorityWidget } from "../../components/dashboard/widgets/contribution-priority.js";
import { SavingRateTrendWidget } from "../../components/dashboard/widgets/saving-rate-trend.js";
import { ActivityFeedWidget } from "../../components/dashboard/widgets/activity-feed.js";
import { DividendCalendarWidget } from "../../components/dashboard/widgets/dividend-calendar.js";
import { LifestyleInflationWidget } from "../../components/dashboard/widgets/lifestyle-inflation.js";

/**
 * Phase 13.5 — dashboard widget registry.
 *
 * Each widget is a self-contained React component (no parent props):
 * it owns its own data-fetching, privacy-mode reads, and render logic.
 * The registry is the single source of truth for which widgets exist
 * and their default order on a fresh user's /dashboard.
 *
 * Adding a new widget: write a wrapper under
 * `web/src/components/dashboard/widgets/<id>.tsx` whose default export
 * matches the `WidgetSpec.Component` shape, then register it here. The
 * customize-mode UI (drag/hide/add) picks it up automatically.
 *
 * `defaultHidden`: widget exists in the library but isn't shown on the
 * default dashboard layout. The lifestyle-inflation tile uses this —
 * it already returns null when no categories are flagged, but explicit
 * registration means a user can pin it permanently to surface as soon
 * as the audit fires.
 *
 * Mobile (Phase 14) will read the same registry but only register the
 * subset of widgets with mobile renderers. Web is read-write today.
 */

export interface WidgetProps {
  readonly instance_config?: Record<string, unknown>;
}

export interface WidgetSpec {
  readonly widget_id: string;
  readonly label: string;
  readonly description: string;
  readonly defaultHidden?: boolean;
  readonly Component: ComponentType<WidgetProps>;
}

export const WIDGETS: readonly WidgetSpec[] = [
  {
    widget_id: "net-worth-hero",
    label: "Net worth hero",
    description:
      "The headline net-worth card with delta pill and 24-month chart. Almost always pinned at the top.",
    Component: NetWorthHeroWidget,
  },
  {
    widget_id: "breakdown",
    label: "Net worth breakdown",
    description:
      "By account / by type / by owner — three views of where the money sits.",
    Component: BreakdownWidget,
  },
  {
    widget_id: "cash-flow-sparkline",
    label: "Cash flow sparkline",
    description:
      "12-week projected cash balance based on bills + income schedules + spending velocity.",
    Component: CashFlowSparklineWidget,
  },
  {
    widget_id: "decisions-revisit",
    label: "Decisions to revisit",
    description: "Saved decisions whose revisit date is approaching.",
    Component: DecisionsRevisitWidgetWrapper,
  },
  {
    widget_id: "messages",
    label: "Messages inbox",
    description: "Unread spousal threads pinned to entities.",
    Component: MessagesWidget,
  },
  {
    widget_id: "contribution-priority",
    label: "Contribution priority",
    description:
      "Where the next dollar should go (Bogleheads investment-order rungs vs YTD).",
    Component: ContributionPriorityWidget,
  },
  {
    widget_id: "saving-rate-trend",
    label: "Saving rate trend",
    description:
      "12-month trailing saving-rate sparkline + on-track / slipping / critical pill.",
    Component: SavingRateTrendWidget,
  },
  {
    widget_id: "activity-feed",
    label: "Activity feed",
    description:
      "Round-number net-worth crossings, FIRE-progress milestones, anniversaries.",
    Component: ActivityFeedWidget,
  },
  {
    widget_id: "dividend-calendar",
    label: "Dividend calendar",
    description:
      "Next 90 days of expected dividend payments across your holdings.",
    Component: DividendCalendarWidget,
  },
  {
    widget_id: "lifestyle-inflation",
    label: "Lifestyle inflation flags",
    description:
      "Categories where YoY spending is outpacing income growth. Hidden until the monthly audit flags something.",
    defaultHidden: true,
    Component: LifestyleInflationWidget,
  },
];

export function widgetById(widget_id: string): WidgetSpec | undefined {
  return WIDGETS.find((w) => w.widget_id === widget_id);
}

export interface WidgetEntry {
  readonly widget_id: string;
  readonly position: number;
  readonly hidden?: boolean;
  readonly instance_config?: Record<string, unknown>;
}

export interface DashboardLayout {
  readonly widgets: readonly WidgetEntry[];
}

/**
 * Default layout for fresh users — every widget appears in canonical
 * order; LifestyleInflation defaults to hidden (can be added back via
 * the customize drawer).
 */
export const DEFAULT_LAYOUT: DashboardLayout = {
  widgets: WIDGETS.map((w, i) => ({
    widget_id: w.widget_id,
    position: i,
    hidden: w.defaultHidden ?? false,
  })),
};

/**
 * Resolve a stored layout against the current registry: drops widgets
 * that no longer exist (graceful schema drift), appends newly-registered
 * widgets at the end (hidden if they default-hidden, visible otherwise).
 */
export function resolveLayout(
  stored: DashboardLayout | null | undefined,
): DashboardLayout {
  if (!stored || stored.widgets.length === 0) return DEFAULT_LAYOUT;
  const known = new Set(WIDGETS.map((w) => w.widget_id));
  const filtered = stored.widgets
    .filter((w) => known.has(w.widget_id))
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((w, i) => ({ ...w, position: i }));
  const present = new Set(filtered.map((w) => w.widget_id));
  const additions = WIDGETS.filter((w) => !present.has(w.widget_id)).map(
    (w, i) => ({
      widget_id: w.widget_id,
      position: filtered.length + i,
      hidden: w.defaultHidden ?? false,
    }),
  );
  return { widgets: [...filtered, ...additions] };
}
